// The Pricing dialog: what the reseller sells one TLD for, as a grid of the terms KilatDomain
// offers × register, transfer and renewal in rupiah (money-input.tsx), each cell with the
// wholesale price under it. An empty price is not sold; an action KilatDomain does not offer for
// the term has no cell. A price below wholesale is marked as it is typed — and when the dialog
// opens, if wholesale has risen since — and nothing is sent until it is fixed. The whole grid is
// saved at once. A reseller not active yet reads it: no cell to type in, nothing to save.
// Closing the dialog unmounts the form, so it opens on what is saved.
import {
  PRICE_ACTIONS,
  SetRetailPricesRequest,
  type PriceAction,
  type ResellerTld,
} from "@kilat/contracts/reseller-pricing";
import { parseMoney } from "@kilat/contracts/money";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldGroup } from "@/components/ui/field";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError } from "@/lib/api-client";
import { m } from "@/lib/paraglide/messages.js";
import { formatIDR } from "../money";
import { MoneyInput } from "../money-input";
import { pricingError } from "../pricing-error";
import { belowWholesale, cellKey, priceRows, refusedCells, toPrices, type PriceRow } from "./price-input";
import { useSetRetailPrices } from "./mutation";

const ACTION_LABELS: Record<PriceAction, () => string> = {
  register: m.tld_pricing_column_register,
  transfer: m.tld_pricing_column_transfer,
  renew: m.tld_pricing_column_renew,
};

type Props = { tld: ResellerTld; editable: boolean };

function PricesForm({ tld, editable, onClose }: Props & { onClose: () => void }) {
  const [rows, setRows] = useState<PriceRow[]>(() => priceRows(tld.terms));
  const save = useSetRetailPrices(tld.id);
  const name = `.${tld.tld}`;

  const below = belowWholesale(rows, tld.terms);
  // What the API refused stays marked until the form changes again.
  const refused = save.error instanceof ApiError ? refusedCells(save.error.details) : new Set<string>();
  const marked = new Set([...below, ...refused]);

  const change = (index: number, patch: Partial<PriceRow>) => {
    save.reset();
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    if (below.size > 0) return;
    save.mutate(SetRetailPricesRequest.parse(toPrices(rows)), {
      onSuccess: () => {
        toast.success(m.reseller_pricing_saved({ tld: name }));
        onClose();
      },
    });
  }

  const error = below.size > 0 ? m.reseller_pricing_below() : pricingError(save.error);
  return (
    <form onSubmit={submit} noValidate>
      <FieldGroup>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{m.tld_pricing_column_term()}</TableHead>
              {PRICE_ACTIONS.map((action) => (
                <TableHead key={action}>{ACTION_LABELS[action]()}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.years}>
                <TableCell>{m.tld_pricing_costs_years({ years: row.years })}</TableCell>
                {PRICE_ACTIONS.map((action) => {
                  const paid = tld.terms[index]?.wholesale[action] ?? null;
                  return (
                    <TableCell key={action}>
                      {paid === null ? (
                        m.reseller_pricing_not_offered()
                      ) : (
                        <Field>
                          <MoneyInput
                            className="min-w-28"
                            disabled={!editable}
                            aria-label={m.tld_pricing_cost_label({ action: ACTION_LABELS[action](), years: row.years })}
                            aria-invalid={marked.has(cellKey(index, action))}
                            value={row[action]}
                            onChange={(amount) => change(index, { [action]: amount })}
                          />
                          <FieldDescription>
                            {m.reseller_pricing_wholesale({ price: formatIDR(parseMoney(paid)) })}
                          </FieldDescription>
                        </Field>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {m.tld_pricing_close()}
          </Button>
          {editable && (
            <Button type="submit" disabled={save.isPending}>
              {m.tld_pricing_costs_save()}
            </Button>
          )}
        </DialogFooter>
      </FieldGroup>
    </form>
  );
}

export function SetTldPriceDialog({ tld, editable }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>{m.tld_pricing_open()}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m.reseller_pricing_prices_title({ tld: `.${tld.tld}` })}</DialogTitle>
          <DialogDescription>{m.reseller_pricing_prices_description()}</DialogDescription>
        </DialogHeader>
        <PricesForm tld={tld} editable={editable} onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
